/* ==========================================================================
   Stacks — family sculpture studio
   The customer picks how many they are (2–7), types a name and picks a colour for each, and
   watches the real parts in 3D. The model is family-gen.js on manifold (the same code the maker
   prints from); every order line carries the design code and one STL per figure.
   ========================================================================== */
import * as FG from 'family-gen';
import TABLE from 'family-bodies';
import { fontProvider, loadStyleFonts, symbolShapes, creaseNormals } from 'name-holder-gen';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const MANIFOLD = 'https://cdn.jsdelivr.net/npm/manifold-3d@3.5.4/manifold.js';

/* ---- copy: English and Egyptian Arabic ---- */
const STR = {
  en: {
    live: 'Live 3D', loading: 'Shaping your family', building: 'Carving the names',
    step_count: 'How many are you?', count_hint: 'One figure each — tallest at the back, the youngest in front.',
    step_members: 'Names and colours', members_hint: 'Tallest first, the same order they stand in. First names read best.',
    figure: 'Figure {n}', tallest: 'tallest', youngest: 'youngest', name_ph: 'Name (optional)',
    colour: 'Colour', fits: 'Fits', partly: 'Partly behind the figure in front — reads in full when you lift it out, like the photos.',
    none: 'Too long for this figure — try a shorter name or a nickname.', missing: 'We can’t carve: {c}',
    step_heart: 'A heart for the youngest', heart_on: 'The smallest figure holds a heart', heart_sub: 'A separate little heart that presses into its chest, in its own colour.',
    heart_colour: 'Heart colour', step_font: 'Letters', font_classic: 'Classic', font_soft: 'Rounded',
    presets: 'Quick looks', p_gold: 'Gold & white', p_mono: 'All white', p_night: 'Black & gold', p_clay: 'Clay & bone',
    add: 'Add to cart', buy: 'Buy now', added: 'Added', adding: 'Adding…', err_cart: 'Couldn’t add it — please try again.',
    err_fit: 'One name is too long for its figure.', err_loading: 'One moment — still carving.',
    explode: 'Show the names', together: 'Stand together', reset: 'Reset view',
    webgl_off: 'Your browser can’t show 3D here — you can still order; we’ll send a preview before printing.',
    summary: '{n} figures · {h} cm tall', lead: 'Made to order in {d}', cod: 'Cash on delivery available',
    no_supports: 'Every figure prints upright, no supports — checked on our slicer.',
    prop_family: 'Family', prop_fig: 'Figure {n} ({h} cm)', prop_heart: 'Heart', prop_font: 'Letters', prop_design: 'Design',
    no_name: 'no name', heart_none: 'none', drag: 'Drag to turn · pinch to zoom',
  },
  ar: {
    live: '3D مباشر', loading: 'بنجهّز عيلتك', building: 'بنحفر الأسامي',
    step_count: 'انتوا كام فرد؟', count_hint: 'شكل لكل فرد — الأطول ورا والأصغر قدّام.',
    step_members: 'الأسامي والألوان', members_hint: 'من الأطول للأصغر، بنفس ترتيب وقوفهم. الاسم الأول بيبان أحلى.',
    figure: 'الشكل {n}', tallest: 'الأطول', youngest: 'الأصغر', name_ph: 'الاسم (اختياري)',
    colour: 'اللون', fits: 'مظبوط', partly: 'جزء من الاسم ورا الشكل اللي قدّامه — بيبان كامل لما ترفعه، زي الصور.',
    none: 'الاسم أطول من الشكل ده — جرّب اسم أقصر أو اسم دلع.', missing: 'مش هنقدر نحفر: {c}',
    step_heart: 'قلب للأصغر', heart_on: 'أصغر شكل شايل قلب', heart_sub: 'قلب صغير منفصل بيتركّب في صدره، بلونه هو.',
    heart_colour: 'لون القلب', step_font: 'شكل الحروف', font_classic: 'كلاسيك', font_soft: 'ناعم',
    presets: 'اختيارات سريعة', p_gold: 'دهبي وأبيض', p_mono: 'كله أبيض', p_night: 'أسود ودهبي', p_clay: 'طوبي وعاجي',
    add: 'أضف للسلة', buy: 'اشتري دلوقتي', added: 'اتضاف', adding: 'بنضيف…', err_cart: 'ماعرفناش نضيفه — جرّب تاني.',
    err_fit: 'فيه اسم أطول من الشكل بتاعه.', err_loading: 'ثانية واحدة — لسه بنحفر.',
    explode: 'وريني الأسامي', together: 'رجّعهم مع بعض', reset: 'رجّع الزاوية',
    webgl_off: 'المتصفح مش بيعرض 3D هنا — تقدر تطلب عادي، وهنبعتلك صورة قبل الطباعة.',
    summary: '{n} أفراد · طول {h} سم', lead: 'بيتعمل مخصوص في {d}', cod: 'الدفع عند الاستلام متاح',
    no_supports: 'كل شكل بيتطبع واقف من غير دعامات — اتجرّب على السلايسر عندنا.',
    prop_family: 'العيلة', prop_fig: 'الشكل {n} ({h} سم)', prop_heart: 'القلب', prop_font: 'الحروف', prop_design: 'التصميم',
    no_name: 'من غير اسم', heart_none: 'من غير', drag: 'اسحب عشان تلفّ · قرّب بصباعين',
  },
};

function parseColors(raw) {
  return String(raw || '').split(/\r?\n/).map((l) => l.split('|').map((s) => s.trim()))
    .filter((p) => p.length >= 4 && /^#[0-9a-f]{6}$/i.test(p[3]))
    .map(([id, en, ar, hex, finish]) => ({ id, en, ar: ar || en, hex, finish: finish || 'basic' }));
}
function money(cents, fmt = '{{amount}}') {
  const v = (cents / 100).toFixed(2), n = Number(v);
  const plain = n % 1 === 0 ? n.toLocaleString('en-US') : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 });
  return fmt.replace(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator)\s*\}\}/, plain);
}
const svg = (d, size = 18) => `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const ICON = {
  spread: '<path d="M4 12h16M4 12l3-3M4 12l3 3M20 12l-3-3M20 12l-3 3"/>',
  reset: '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/>',
  heart: '<path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/>',
  check: '<path d="M5 12.5l4.2 4.2L19 7"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/>',
  warn: '<path d="M12 3l9.5 17h-19z"/><path d="M12 10v4M12 17h.01"/>',
};

/* ---------- the 3D stage ---------- */
async function createStage(host, { onReady } = {}) {
  const THREE = await import('three');
  const [{ OrbitControls }, { RoomEnvironment }] = await Promise.all([
    import('three/addons/controls/OrbitControls.js'),
    import('three/addons/environments/RoomEnvironment.js'),
  ]);
  let renderer;
  try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true }); } catch (e) { throw new Error('webgl'); }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  host.appendChild(renderer.domElement);
  renderer.domElement.setAttribute('role', 'img');
  renderer.domElement.tabIndex = 0;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.6;
  const camera = new THREE.PerspectiveCamera(26, 1, 5, 8000);
  const key = new THREE.DirectionalLight(0xffffff, 2.8);
  key.position.set(-260, 420, 380); key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048); key.shadow.radius = 4; key.shadow.bias = -0.0004; key.shadow.normalBias = 0.3;
  Object.assign(key.shadow.camera, { left: -220, right: 220, top: 260, bottom: -120, near: 10, far: 1500 });
  const rim = new THREE.DirectionalLight(0xfff1dc, 0.9); rim.position.set(300, 200, -300);
  scene.add(key, key.target, rim, new THREE.HemisphereLight(0xffffff, 0xe7e1d4, 0.5));
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(4000, 4000), new THREE.ShadowMaterial({ opacity: 0.18 }));
  ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; scene.add(ground);

  /* the family: in the model's frame (Z up, front at −Y) turned to three's (Y up, front at +Z) */
  const family = new THREE.Group(); family.rotation.x = -Math.PI / 2; scene.add(family);
  const controls = new OrbitControls(camera, renderer.domElement);
  Object.assign(controls, { enableDamping: true, dampingFactor: 0.08, enablePan: false, minPolarAngle: 0.25, maxPolarAngle: Math.PI / 2 - 0.04, rotateSpeed: 0.8 });
  let userMoved = false;
  controls.addEventListener('start', () => { userMoved = true; });

  const figures = [];   // { group, mesh, heart, mat, off: current offset, target }
  const matFor = (c) => {
    const finish = c?.finish || 'basic';
    return new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(c?.hex || '#eeeeee'),
      roughness: finish === 'silk' ? 0.42 : finish === 'matte' ? 0.86 : 0.6,
      metalness: finish === 'silk' ? 0.12 : 0,
      sheen: finish === 'silk' ? 0.25 : 0, clearcoat: finish === 'basic' ? 0.06 : 0,
    });
  };
  const geoOf = (mesh) => {
    const g = new THREE.BufferGeometry();
    const np = mesh.numProp, v = mesh.vertProperties, pos = new Float32Array((v.length / np) * 3);
    for (let i = 0, j = 0; i < v.length; i += np, j += 3) { pos[j] = v[i]; pos[j + 1] = v[i + 1]; pos[j + 2] = v[i + 2]; }
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setIndex(new THREE.BufferAttribute(new Uint32Array(mesh.triVerts), 1));
    const flat = g.toNonIndexed(); g.dispose();
    return creaseNormals(THREE, flat, 34);
  };
  function ensure(n) {
    while (figures.length < n) {
      const group = new THREE.Group(); family.add(group);
      const mat = matFor(null);
      const mesh = new THREE.Mesh(new THREE.BufferGeometry(), mat); mesh.castShadow = true; mesh.receiveShadow = true; group.add(mesh);
      figures.push({ group, mesh, mat, heart: null, off: new THREE.Vector3(), target: new THREE.Vector3(), pop: 0 });
    }
    figures.forEach((f, i) => { f.group.visible = i < n; });
  }
  function setFigure(i, mesh, color, pop = false) {
    ensure(i + 1);
    const f = figures[i];
    f.mesh.geometry.dispose();
    f.mesh.geometry = geoOf(mesh);
    setColor(i, color);
    if (pop && !reduced()) f.pop = 1;
  }
  function setColor(i, c) {
    const f = figures[i]; if (!f) return;
    const m = matFor(c); m.transparent = f.mat.transparent; m.opacity = f.mat.opacity;
    f.mat.dispose(); f.mat = m; f.mesh.material = m;
  }
  function setHeart(i, mesh, at, yf, color) {
    for (const f of figures) if (f.heart) { f.group.remove(f.heart); f.heart.geometry.dispose(); f.heart = null; }
    if (!mesh) return;
    const f = figures[i];
    const h = new THREE.Mesh(geoOf(mesh), matFor(color)); h.castShadow = true;
    /* the piece lies flat (z up); stand it in its pocket: its back on the pocket floor */
    h.rotation.x = Math.PI / 2;
    h.position.set(at[0], yf + FG.SPEC.heart.depth, at[1]);
    f.group.add(h); f.heart = h;
  }
  function setHeartColor(c) { for (const f of figures) if (f.heart) { f.heart.material.dispose(); f.heart.material = matFor(c); } }
  function setOffsets(offs) { offs.forEach((o, i) => { ensure(i + 1); figures[i].target.set(o[0], o[1], o[2]); }); }
  function focus(i) {
    figures.forEach((f, k) => {
      const ghost = i != null && k !== i;
      f.mat.transparent = ghost; f.mat.opacity = ghost ? 0.16 : 1; f.mat.depthWrite = !ghost; f.mat.needsUpdate = true;
      if (f.heart) { f.heart.material.transparent = ghost; f.heart.material.opacity = ghost ? 0.16 : 1; f.heart.material.needsUpdate = true; }
    });
  }
  /* frame the family: from the front, a little above and to the side, like a shelf photo */
  let frameBox = null;
  function frame(box, { instant = false } = {}) {
    frameBox = box;
    const [x0, y0, z0, x1, y1, z1] = box;   // model frame
    const c = new THREE.Vector3((x0 + x1) / 2, (z0 + z1) / 2, -(y0 + y1) / 2);
    const size = Math.max(x1 - x0, (z1 - z0) * 1.05, (y1 - y0) * 0.8);
    const aspect = renderer.domElement.clientWidth / Math.max(1, renderer.domElement.clientHeight);
    const fit = size / (2 * Math.tan((camera.fov * Math.PI) / 360)) / Math.min(1, aspect * 0.95);
    const dist = fit * 1.5;
    const dir = new THREE.Vector3(-0.32, 0.26, 1).normalize();
    const to = c.clone().add(dir.multiplyScalar(dist));
    if (instant || !userMoved) { camera.position.copy(to); controls.target.copy(c); }
    controls.minDistance = dist * 0.45; controls.maxDistance = dist * 2.2;
    key.target.position.copy(c);
  }
  function resize() {
    const w = host.clientWidth, h = host.clientHeight;
    renderer.setSize(w, h, false); camera.aspect = w / Math.max(1, h); camera.updateProjectionMatrix();
  }
  new ResizeObserver(() => { resize(); if (frameBox) frame(frameBox, { instant: !userMoved }); }).observe(host);
  resize();
  let t0 = performance.now(), sway = !reduced();
  function loop(now) {
    const dt = Math.min(0.05, (now - t0) / 1000); t0 = now;
    for (const f of figures) {
      f.off.lerp(f.target, reduced() ? 1 : 1 - Math.pow(0.001, dt));
      f.group.position.copy(f.off);
      if (f.pop > 0) { f.pop = Math.max(0, f.pop - dt * 3); const s = 1 + Math.sin(f.pop * Math.PI) * 0.025; f.group.scale.setScalar(s); } else f.group.scale.setScalar(1);
    }
    if (sway && !userMoved) family.rotation.z = Math.sin(now / 2600) * 0.12;
    controls.update();
    renderer.render(scene, camera);
    requestAnimationFrame(loop);
  }
  requestAnimationFrame(loop);
  onReady?.();
  return {
    THREE, ensure, setFigure, setColor, setHeart, setHeartColor, setOffsets, focus, frame,
    reset() { userMoved = false; family.rotation.z = 0; if (frameBox) frame(frameBox, { instant: true }); },
    snapshot(size = 480) {
      const prev = [renderer.domElement.width, renderer.domElement.height];
      renderer.setSize(size, size, false); camera.aspect = 1; camera.updateProjectionMatrix();
      renderer.render(scene, camera);
      const url = renderer.domElement.toDataURL('image/jpeg', 0.88);
      resize();
      void prev;
      return url;
    },
  };
}

/* ---------- the studio ---------- */
async function boot(root) {
  const cfg = JSON.parse($('[data-fam-config]', root).textContent);
  const lang = /^ar/i.test(cfg.locale) ? 'ar' : 'en';
  const T = STR[lang];
  const t = (k, vars = {}) => String(T[k] ?? STR.en[k] ?? k).replace(/\{(\w+)\}/g, (_, v) => vars[v] ?? '');
  const colors = parseColors(cfg.colors);
  const colorOf = (id) => colors.find((c) => c.id === id) || colors[0];
  const cname = (c) => (lang === 'ar' ? c.ar : c.en);
  const product = cfg.product;
  const maxChars = cfg.maxChars || 10;
  const stageEl = $('[data-fam-stage]', root), controls = $('[data-fam-controls]', root), note = $('[data-fam-note]', root);
  const presets = [
    { id: 'gold', a: cfg.defaults.a, b: cfg.defaults.b },
    { id: 'night', a: 'black', b: 'gold' },
    { id: 'clay', a: 'terracotta', b: 'bone' },
    { id: 'mono', a: 'jade-white', b: 'jade-white' },
  ].filter((p) => colors.some((c) => c.id === p.a) && colors.some((c) => c.id === p.b));

  /* state: names beyond the family size are kept, so going 5 → 3 → 5 loses nothing */
  const url = new URL(location.href);
  const fromCode = FG.decodeFamily(url.searchParams.get('fam'));
  const members = Array.from({ length: FG.SPEC.maxFigures }, (_, i) => ({ name: '', color: i % 2 === 0 ? cfg.defaults.a : cfg.defaults.b }));
  let state = { count: cfg.defaults.count || 3, heart: null, font: 'classic', heartColor: cfg.defaults.heart || 'red' };
  if (fromCode) {
    state.count = fromCode.count; state.heart = fromCode.heart; state.font = fromCode.font;
    fromCode.members.forEach((m, i) => { if (members[i]) Object.assign(members[i], m); });
  }
  const heartOn = () => (state.heart === null ? state.count % 2 === 1 : state.heart);
  const params = () => FG.normalizeFamily({ count: state.count, heart: heartOn(), font: state.font, members }, { colors, maxChars, clean: (s, n) => cleanName(s, n) });
  function cleanName(s, n = maxChars) {
    return Array.from(String(s ?? '').normalize('NFC').replace(/[ً-ٰٟ]/g, '').replace(/[^\p{L}\p{M}\s'-]/gu, '').replace(/\s+/g, ' ').trim()).slice(0, n).join('');
  }

  /* ---- controls ---- */
  const heights = FG.SPEC.heights;
  controls.innerHTML = `
    <section class="fam-card">
      <header class="fam-card__head"><span class="fam-step">1</span><h2 class="fam-card__title">${esc(t('step_count'))}</h2></header>
      <p class="fam-card__hint">${esc(t('count_hint'))}</p>
      <div class="fam-counts" role="radiogroup" aria-label="${esc(t('step_count'))}">
        ${[2, 3, 4, 5, 6, 7].map((n) => `<button type="button" class="fam-count" role="radio" data-count="${n}" aria-checked="false">
          <span class="fam-count__people" aria-hidden="true">${Array.from({ length: n }, (_, k) => `<i style="--h:${(heights[k] / heights[0]).toFixed(2)}"></i>`).join('')}</span>
          <span class="fam-count__n">${n}</span><span class="fam-count__price num" data-count-price="${n}"></span></button>`).join('')}
      </div>
    </section>
    <section class="fam-card">
      <header class="fam-card__head"><span class="fam-step">2</span><h2 class="fam-card__title">${esc(t('step_members'))}</h2></header>
      <p class="fam-card__hint">${esc(t('members_hint'))}</p>
      <div class="fam-presets" aria-label="${esc(t('presets'))}">${presets.map((p) => `<button type="button" class="fam-preset" data-preset="${p.id}"><span style="--a:${colorOf(p.a).hex};--b:${colorOf(p.b).hex}"></span>${esc(t('p_' + p.id))}</button>`).join('')}</div>
      <ol class="fam-members" data-members></ol>
    </section>
    <section class="fam-card">
      <header class="fam-card__head"><span class="fam-step">3</span><h2 class="fam-card__title">${esc(t('step_heart'))}</h2></header>
      <label class="fam-switch"><input type="checkbox" data-heart><span class="fam-switch__ui" aria-hidden="true"></span>
        <span><b>${esc(t('heart_on'))}</b><small>${esc(t('heart_sub'))}</small></span></label>
      <div class="fam-heart-colors" data-heart-colors role="radiogroup" aria-label="${esc(t('heart_colour'))}"></div>
    </section>
    <section class="fam-card">
      <header class="fam-card__head"><span class="fam-step">4</span><h2 class="fam-card__title">${esc(t('step_font'))}</h2></header>
      <div class="fam-fonts" role="radiogroup">${FG.FONTS.map((f) => `<button type="button" class="fam-font" role="radio" data-font="${f.id}" aria-checked="false" style="font-family:'fam-${f.id}', system-ui"><span>${lang === 'ar' ? 'أحمد' : 'Ahmed'}</span><small>${esc(t('font_' + f.id))}</small></button>`).join('')}</div>
    </section>
    <section class="fam-card fam-buy" data-buy>
      <div class="fam-buy__top"><div><div class="fam-price num" data-price></div><div class="fam-compare num" data-compare></div></div><div class="fam-buy__meta" data-summary></div></div>
      <p class="fam-msg" data-msg role="status" aria-live="polite"></p>
      <div class="fam-buy__row">
        <button type="button" class="btn btn--primary fam-add" data-add>${esc(t('add'))}</button>
        <button type="button" class="btn btn--ghost fam-buynow" data-buynow>${esc(t('buy'))}</button>
      </div>
      <ul class="fam-trust"><li>${svg(ICON.check, 15)}${esc(t('cod'))}</li><li>${svg(ICON.check, 15)}${esc(t('lead', { d: cfg.lead }))}</li><li>${svg(ICON.check, 15)}${esc(t('no_supports'))}</li></ul>
    </section>`;
  const dock = document.createElement('div');
  dock.className = 'fam-dock'; dock.dataset.bottomBar = ''; dock.dataset.bottomBarWhen = 'is-on'; dock.innerHTML = `<span class="fam-dock__price num" data-dock-price></span><button type="button" class="btn btn--primary" data-dock-add>${esc(t('add'))}</button>`;
  root.appendChild(dock);
  /* the font faces, for the chips and name fields */
  const faceCss = FG.FONTS.map((f) => {
    const files = [`https://cdn.jsdelivr.net/npm/@fontsource/${f.latin[0]}@5.3.0/files/${f.latin[0]}-latin-${f.latin[1]}-normal.woff2`, `https://cdn.jsdelivr.net/npm/@fontsource/${f.arabic[0]}@5.3.0/files/${f.arabic[0]}-arabic-${f.arabic[1]}-normal.woff2`];
    return `@font-face{font-family:'fam-${f.id}';src:url(${files[0]}) format('woff2');unicode-range:U+0000-024F;font-display:swap}@font-face{font-family:'fam-${f.id}';src:url(${files[1]}) format('woff2');unicode-range:U+0600-06FF,U+FB50-FEFF;font-display:swap}`;
  }).join('');
  document.head.insertAdjacentHTML('beforeend', `<style>${faceCss}</style>`);

  const list = $('[data-members]', controls);
  const rowState = [];   // per figure: { fit, missing }
  function renderMembers() {
    const n = state.count;
    list.innerHTML = Array.from({ length: n }, (_, i) => {
      const m = members[i], c = colorOf(m.color);
      const h = (heights[i] / 10).toFixed(1).replace(/\.0$/, '');
      const tag = i === 0 ? t('tallest') : i === n - 1 ? t('youngest') : '';
      return `<li class="fam-member" data-i="${i}" style="--c:${c.hex}">
        <div class="fam-member__head"><span class="fam-member__dot" aria-hidden="true"></span>
          <b>${esc(t('figure', { n: i + 1 }))}</b><span class="fam-member__h num">${h} cm</span>${tag ? `<span class="fam-tag">${esc(tag)}</span>` : ''}
          <span class="fam-member__fit" data-fit aria-live="polite"></span></div>
        <div class="fam-member__row">
          <label class="fam-name"><span class="sr-only">${esc(t('figure', { n: i + 1 }))}</span>
            <input type="text" inputmode="text" autocomplete="off" spellcheck="false" maxlength="${maxChars + 4}" value="${esc(m.name)}" placeholder="${esc(t('name_ph'))}" data-name dir="auto" style="font-family:'fam-${state.font}', system-ui">
            <span class="fam-name__count num" data-len>${Array.from(m.name).length}/${maxChars}</span></label>
          <button type="button" class="fam-swatch" data-swatch aria-expanded="false" aria-label="${esc(t('colour'))}: ${esc(cname(c))}"><span></span><em>${esc(cname(c))}</em></button>
        </div>
        <div class="fam-palette" data-palette hidden role="radiogroup" aria-label="${esc(t('colour'))}">${colors.map((cc) => `<button type="button" role="radio" aria-checked="${cc.id === m.color}" data-color="${cc.id}" title="${esc(cname(cc))}" style="--c:${cc.hex}" class="fam-chip fam-chip--${cc.finish}"><span class="sr-only">${esc(cname(cc))}</span></button>`).join('')}</div>
        <p class="fam-member__note" data-note></p>
      </li>`;
    }).join('');
    syncFits();
  }
  function syncFits() {
    $$('.fam-member', list).forEach((li) => {
      const i = +li.dataset.i, r = rowState[i] || {};
      const fit = $('[data-fit]', li), noteEl = $('[data-note]', li);
      li.dataset.fit = r.fit || '';
      fit.innerHTML = r.fit === 'full' ? `${svg(ICON.check, 14)}${esc(t('fits'))}` : r.fit === 'partly' ? svg(ICON.info, 14) : r.fit === 'none' ? svg(ICON.warn, 14) : '';
      noteEl.textContent = r.missing?.length ? t('missing', { c: r.missing.join(' ') }) : r.fit === 'none' ? t('none') : r.fit === 'partly' ? t('partly') : '';
    });
  }
  function syncControls() {
    $$('.fam-count', controls).forEach((b) => b.setAttribute('aria-checked', String(+b.dataset.count === state.count)));
    $$('.fam-font', controls).forEach((b) => b.setAttribute('aria-checked', String(b.dataset.font === state.font)));
    $('[data-heart]', controls).checked = heartOn();
    const hc = $('[data-heart-colors]', controls);
    hc.hidden = !heartOn();
    hc.innerHTML = colors.map((c) => `<button type="button" role="radio" aria-checked="${c.id === state.heartColor}" data-heart-color="${c.id}" title="${esc(cname(c))}" style="--c:${c.hex}" class="fam-chip fam-chip--${c.finish}"><span class="sr-only">${esc(cname(c))}</span></button>`).join('');
    for (const n of [2, 3, 4, 5, 6, 7]) { const v = variantFor(n); const el = $(`[data-count-price="${n}"]`, controls); if (el) el.textContent = v ? money(v.price, cfg.money) : ''; }
    syncSummary();
  }
  function variantFor(n) {
    return product.variants.find((v) => v.options.some((o) => String(o).replace(/[^\d]/g, '') === String(n))) || null;
  }
  function syncSummary() {
    const v = variantFor(state.count);
    const price = v ? money(v.price, cfg.money) : '';
    $('[data-price]', controls).textContent = price;
    const cmp = $('[data-compare]', controls);
    cmp.textContent = v && v.compare_at_price > v.price ? money(v.compare_at_price, cfg.money) : '';
    $('[data-summary]', controls).textContent = t('summary', { n: state.count, h: (heights[0] / 10).toFixed(1).replace(/\.0$/, '') });
    $('[data-dock-price]', root).textContent = price;
    for (const b of [$('[data-add]', controls), $('[data-dock-add]', root)]) if (!b.dataset.done) b.textContent = t('add');
  }

  /* ---- events ---- */
  controls.addEventListener('click', (e) => {
    const cnt = e.target.closest('[data-count]');
    if (cnt) { state.count = +cnt.dataset.count; renderMembers(); syncControls(); rebuild({ structure: true }); return; }
    const font = e.target.closest('[data-font]');
    if (font) { state.font = font.dataset.font; $$('[data-name]', list).forEach((inp) => { inp.style.fontFamily = `'fam-${state.font}', system-ui`; }); syncControls(); rebuild({ names: true }); return; }
    const pre = e.target.closest('[data-preset]');
    if (pre) {
      const p = presets.find((x) => x.id === pre.dataset.preset);
      members.forEach((m, i) => { m.color = i % 2 === 0 ? p.a : p.b; });
      renderMembers(); recolour(); return;
    }
    const sw = e.target.closest('[data-swatch]');
    if (sw) {
      const li = sw.closest('.fam-member'), pal = $('[data-palette]', li), open = pal.hidden;
      $$('[data-palette]', list).forEach((p) => { p.hidden = true; }); $$('[data-swatch]', list).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      pal.hidden = !open; sw.setAttribute('aria-expanded', String(open));
      if (open) stage?.focus(+li.dataset.i);
      return;
    }
    const chip = e.target.closest('[data-color]');
    if (chip) {
      const li = chip.closest('.fam-member'), i = +li.dataset.i, c = colorOf(chip.dataset.color);
      members[i].color = c.id;
      li.style.setProperty('--c', c.hex);
      $('[data-swatch] em', li).textContent = cname(c);
      $$('[data-color]', li).forEach((b) => b.setAttribute('aria-checked', String(b === chip)));
      stage?.setColor(i, c);
      return;
    }
    const hc = e.target.closest('[data-heart-color]');
    if (hc) { state.heartColor = hc.dataset.heartColor; syncControls(); stage?.setHeartColor(colorOf(state.heartColor)); return; }
  });
  $('[data-heart]', controls).addEventListener('change', (e) => { state.heart = e.target.checked; syncControls(); rebuild({ structure: true }); });
  list.addEventListener('input', (e) => {
    const inp = e.target.closest('[data-name]'); if (!inp) return;
    const li = inp.closest('.fam-member'), i = +li.dataset.i;
    const clean = cleanName(inp.value);
    if (clean !== inp.value.trim() && Array.from(inp.value).length > maxChars) inp.value = clean;
    members[i].name = clean;
    $('[data-len]', li).textContent = `${Array.from(clean).length}/${maxChars}`;
    rebuild({ names: [i] });
  });
  list.addEventListener('focusin', (e) => { const li = e.target.closest('.fam-member'); if (li && e.target.matches('[data-name]')) { stage?.focus(+li.dataset.i); } });
  list.addEventListener('focusout', () => { setTimeout(() => { if (!list.contains(document.activeElement)) { stage?.focus(null); } }, 60); });
  document.addEventListener('click', (e) => { if (!e.target.closest('.fam-member')) { $$('[data-palette]', list).forEach((p) => { p.hidden = true; }); if (!list.contains(document.activeElement)) stage?.focus(null); } });

  renderMembers(); syncControls();

  /* ---- the model ---- */
  let stage = null, M = null, heartShape = null;
  const providers = new Map();
  const provider = (id) => {
    if (!providers.has(id)) providers.set(id, import('opentype').then((m) => m.default || m).then((ot) => loadStyleFonts(ot, FG.fontOf(id))).then(fontProvider));
    return providers.get(id);
  };
  const bodies = {};          // body key → Manifold, fetched the first time a family size needs it
  const preps = new Map();   // "count|heart" → prepared family
  const solids = [];          // per figure: the engraved solid on screen, and what it was built from
  let exploded = false, prepNow = null, busy = 0;
  const setBusy = (d) => { busy += d; stageEl.dataset.busy = busy > 0 ? '1' : ''; };

  function recolour() { for (let i = 0; i < state.count; i++) stage?.setColor(i, colorOf(members[i].color)); }
  function offsets(prep) {
    /* together: all at home. Apart: each figure steps forward out of the one behind, so every name shows */
    let acc = 0;
    return prep.figs.map((x, i) => {
      if (!exploded || i === 0) return [0, 0, 0];
      acc += (x.fig.recess || 0) + 10 + x.fig.D * 0.15;
      return [0, -acc, 0];
    });
  }
  function box(prep) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = 0;
    const offs = offsets(prep);
    prep.figs.forEach((x, i) => {
      const b = x.body.boundingBox(), o = offs[i];
      x0 = Math.min(x0, b.min[0] + o[0]); x1 = Math.max(x1, b.max[0] + o[0]);
      y0 = Math.min(y0, b.min[1] + o[1]); y1 = Math.max(y1, b.max[1] + o[1]); z1 = Math.max(z1, b.max[2]);
    });
    return [x0, y0, 0, x1, y1, z1];
  }
  let job = 0, timer = null, pendingNames = new Set(), pendingStructure = false;
  function rebuild({ structure = false, names = null } = {}) {
    if (structure) pendingStructure = true;
    if (names === true) for (let i = 0; i < 7; i++) pendingNames.add(i);
    else if (Array.isArray(names)) names.forEach((i) => pendingNames.add(i));
    clearTimeout(timer);
    timer = setTimeout(run, structure ? 30 : 220);
  }
  async function run() {
    if (!M) return;
    const my = ++job;
    const p = params();
    const k = `${p.count}|${p.heart ? 1 : 0}`;
    setBusy(1);
    try {
      if (!preps.has(k)) {
        note.innerHTML = `<span class="fam-dots">${esc(t('loading'))}</span>`;
        await new Promise((r) => setTimeout(r, 30));   // let the message paint
        const keys = FG.bodyKeys(p.count);
        await Promise.all(keys.filter((key) => !bodies[key]).map(async (key) => {
          const { default: data } = await import(`family-body/${key}`);
          bodies[key] = FG.decodeBody(M, data, TABLE.q);
        }));
        if (my !== job) return;
        preps.set(k, FG.prepareFamily(M, FG.planFamily(p, TABLE), { bodies, heartShape }));
      }
      const prep = preps.get(k);
      const structural = pendingStructure || prepNow !== prep;
      pendingStructure = false;
      prepNow = prep;
      const pv = await provider(p.font);
      if (my !== job) return;
      stage.ensure(p.count);
      for (let i = 0; i < p.count; i++) {
        const name = p.members[i].name;
        const had = solids[i];
        if (!structural && had && had.k === k && had.name === name && had.font === p.font) continue;
        const r = FG.engraveFigure(M, prep, i, name, pv);
        had?.solid.delete();
        solids[i] = { solid: r.solid, name, font: p.font, k };
        rowState[i] = { fit: r.fit, missing: r.missing };
        stage.setFigure(i, r.solid.getMesh(), colorOf(members[i].color), !structural);
        if (i % 2 === 1) await new Promise((res) => setTimeout(res, 0));   // keep typing smooth on big families
        if (my !== job) return;
      }
      pendingNames.clear();
      const last = prep.figs[p.count - 1];
      stage.setHeart(p.count - 1, last.heart ? last.heart.getMesh() : null, last.heartAt, last.fig.yf + last.fig.bow, colorOf(state.heartColor));
      stage.setOffsets(offsets(prep));
      if (structural) stage.frame(box(prep));
      note.textContent = '';
      stageEl.dataset.state = 'ready';
      syncFits();
    } catch (e) {
      console.error('[family]', e);
      note.textContent = t('err_cart');
    } finally { setBusy(-1); }
  }

  /* tools on the stage */
  const tools = $('[data-fam-tools]', root);
  tools.innerHTML = `<button type="button" class="fam-tool" data-explode aria-pressed="false">${svg(ICON.spread, 16)}<span>${esc(t('explode'))}</span></button>
    <button type="button" class="fam-tool fam-tool--icon" data-reset aria-label="${esc(t('reset'))}" title="${esc(t('reset'))}">${svg(ICON.reset, 16)}</button>`;
  $('[data-explode]', tools).addEventListener('click', (e) => {
    exploded = !exploded;
    e.currentTarget.setAttribute('aria-pressed', String(exploded));
    $('span', e.currentTarget).textContent = t(exploded ? 'together' : 'explode');
    if (prepNow) { stage.setOffsets(offsets(prepNow)); stage.frame(box(prepNow)); }
  });
  $('[data-reset]', tools).addEventListener('click', () => stage?.reset());
  $('[data-fam-hint]', root).textContent = t('drag');
  $('[data-fam-live]', root).textContent = t('live');

  /* ---- cart ---- */
  const msg = $('[data-msg]', controls);
  let offline = false;   // no 3D here: the order still goes through, the maker rebuilds the files from the code
  function problem() {
    if (offline) return null;
    if (!prepNow || busy > 0) return t('err_loading');
    for (let i = 0; i < state.count; i++) if (rowState[i]?.fit === 'none' || rowState[i]?.missing?.length) return t('err_fit');
    return null;
  }
  function designUrl() {
    const u = new URL(product.url || location.pathname, location.origin);
    u.searchParams.set('fam', FG.encodeFamily(params()));
    return u.toString();
  }
  async function thumb() {
    try {
      const d = stage?.snapshot(480); if (!d) return null;
      const r = await fetch(d); return await r.blob();
    } catch { return null; }
  }
  async function addToCart(button, buyNow = false) {
    clearTimeout(timer);
    if (pendingStructure || pendingNames.size) await run();
    const p = problem();
    if (p) {
      msg.textContent = p; msg.classList.add('is-error');
      const bad = $('.fam-member[data-fit="none"]', list);
      (bad || controls).scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
      return;
    }
    msg.textContent = ''; msg.classList.remove('is-error');
    const v = variantFor(state.count);
    if (!v) { msg.textContent = t('err_cart'); return; }
    const pp = params();
    const fd = new FormData();
    fd.append('id', v.id); fd.append('quantity', '1');
    fd.append(`properties[${t('prop_family')}]`, pp.members.map((m) => m.name || '—').join(' · '));
    pp.members.forEach((m, i) => fd.append(`properties[${t('prop_fig', { n: i + 1, h: (heights[i] / 10).toFixed(1).replace(/\.0$/, '') })}]`, `${m.name || t('no_name')} — ${cname(colorOf(m.color))}`));
    fd.append(`properties[${t('prop_heart')}]`, pp.heart ? cname(colorOf(state.heartColor)) : t('heart_none'));
    fd.append(`properties[${t('prop_font')}]`, t('font_' + pp.font));
    fd.append(`properties[${t('prop_design')}]`, designUrl());
    fd.append('properties[_fam]', FG.encodeFamily(pp));
    fd.append('properties[_fam_heart]', pp.heart ? state.heartColor : '');
    button.classList.add('is-busy'); button.textContent = t('adding');
    if (!offline) {
      const stats = FG.stats(M, solids.slice(0, pp.count).map((s, i) => ({ i, solid: s.solid, heart: i === pp.count - 1 ? prepNow.figs[i].heart : null })));
      fd.append('properties[_fam_spec]', stats.map((s) => `${s.size.map((x) => Math.round(x)).join('x')}mm ${Math.round(s.grams)}g`).join(' | '));
      const img = await thumb();
      if (img) fd.append('properties[_thumb]', img, 'family.jpg');
      /* the print files, built from the very parts on screen: one STL per figure, and the heart */
      const slug = (s) => (s || '').normalize('NFKD').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'name';
      solids.slice(0, pp.count).forEach((s, i) => {
        const stl = FG.meshToSTL(s.solid.getMesh(), `stacks family ${FG.encodeFamily(pp)} #${i + 1}`);
        fd.append(`properties[_stl_${i + 1}]`, new File([stl], `family-${i + 1}-${slug(pp.members[i].name)}-${colorOf(pp.members[i].color).id}.stl`, { type: 'model/stl' }));
      });
      const heart = prepNow.figs[pp.count - 1].heart;
      if (pp.heart && heart) fd.append('properties[_stl_heart]', new File([FG.meshToSTL(heart.getMesh(), 'stacks family heart')], `family-heart-${state.heartColor}.stl`, { type: 'model/stl' }));
    }
    try {
      if (buyNow || typeof window.addToCart !== 'function') {
        const res = await fetch(`${window.Stacks?.routes?.cartAdd || '/cart/add'}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || `cart ${res.status}`);
      } else await window.addToCart(fd, { button });
      if (buyNow) { location.href = '/checkout'; return; }
      if (typeof window.addToCart !== 'function') window.refreshCartUI?.({ open: true });
      button.classList.remove('is-busy');
      for (const b of [$('[data-add]', controls), $('[data-dock-add]', root)]) { b.dataset.done = '1'; b.textContent = `✓ ${t('added')}`; }
      setTimeout(() => { for (const b of [$('[data-add]', controls), $('[data-dock-add]', root)]) delete b.dataset.done; syncSummary(); }, 2600);
    } catch (e) {
      console.error('[family] cart', e);
      button.classList.remove('is-busy'); syncSummary();
      msg.textContent = t('err_cart'); msg.classList.add('is-error');
    }
  }
  $('[data-add]', controls).addEventListener('click', (e) => addToCart(e.currentTarget));
  $('[data-buynow]', controls).addEventListener('click', (e) => addToCart(e.currentTarget, true));
  $('[data-dock-add]', root).addEventListener('click', (e) => addToCart(e.currentTarget));
  /* the phone dock shows once the buy card is out of view */
  new IntersectionObserver(([en]) => dock.classList.toggle('is-on', !en.isIntersecting), { threshold: 0 }).observe($('[data-buy]', controls));

  /* ---- start: 3D and the geometry library load side by side ---- */
  note.innerHTML = `<span class="fam-dots">${esc(t('loading'))}</span>`;
  try {
    const [st, Module] = await Promise.all([createStage($('[data-fam-canvas]', root)), import(MANIFOLD).then((m) => m.default)]);
    stage = st;
    M = await Module(); M.setup();
    heartShape = symbolShapes('heart');
    rebuild({ structure: true, names: true });
    run();
  } catch (e) {
    console.error('[family] boot', e);
    stageEl.dataset.state = 'off';
    note.textContent = t('webgl_off');
    /* without 3D the order still goes through: the maker rebuilds the files from the design code */
    offline = true;
  }
}

for (const root of document.querySelectorAll('[data-fam]')) boot(root);
